import type { AuthProviderId, OneTapAuthConfig } from '@creator/sdk/modules/account/account.model';
import type { AdProvider, DomainConfig, PlaywireAd } from '@creator/sdk/modules/config/config.model';
import { getDomainName, getFirebaseStorageBucket, getGoogleProviderClientId } from '@src/config';
import creatorSdk from '@src/services/creator-sdk';
import store from '@src/store';
import { isMobile } from '@src/utils/utils';

export function getDomainConfig(): DomainConfig {
    return store.getState().config.domainConfig as DomainConfig; // we do not render the app untill the config is loaded.. so its safe to return as 'DomainConfig'
}

export function getDefaultDomainConfig(): DomainConfig {
    return {
        defaultTokenName: '',
        domainDisplayName: 'dɘWeb',
        logoLink: '/',
        signInProviders: ['TWITTER', 'GOOGLE', 'GITHUB'],
        privacyPolicyLink: '',
        termsOfUseLink: '',
        faqLink: '',
        enableForumPicker: false,
        mobileApp: undefined,
    };
}

export function getDomainConfigValue<K extends keyof DomainConfig>(key: K): DomainConfig[K] {
    const config = getDomainConfig();
    return config[key];
}

//Auth providers list
export function getAuthProviderIds(): AuthProviderId[] {
    const signInProviders = getDomainConfigValue('signInProviders');
    const authProviderIds = signInProviders.map(signInProvider => creatorSdk.configModule.helpers.getAuthProviderIdBySignInProvider(signInProvider));
    return authProviderIds;
}

//Get google one tap outh config
export function getGoogleOneTapToAuthConfig(): OneTapAuthConfig {
    return creatorSdk.configModule.helpers.getGoogleOneTapToAuthConfig(getGoogleProviderClientId());
}

export function getLogoSrc(): string {
    return getStorageBucketFileUrl('logo.png');
}

export function getLogoDarkSrc(): string {
    return getStorageBucketFileUrl('logo-dark.png');
}

export function getStorageBucketFileUrl(filename: string): string {
    const storageBucket = getFirebaseStorageBucket();
    const domainName = getDomainName();
    return `https://firebasestorage.googleapis.com/v0/b/${storageBucket}/o/${domainName}%2F${filename}?alt=media`;
}

export function getMessageBoardReactionNames(): string[] {
    return ['thumb-up', 'thumb-down'];
}

export function getDomainMobileApp() {
    return getDomainConfigValue('mobileApp');
}

export function getUrlMapping() {
    return getDomainConfigValue('urlMapping') || {};
}

export function getUrlMappingToken(tokenName: string) {
    const urlMapping = getDomainConfigValue('urlMapping');
    if (!urlMapping) return;

    const { tokens = {} } = urlMapping;
    return tokens[tokenName];
}

// Return url slug if available, if not return tokenName, usefull for links
export function ensureTokenUrl(tokenName: string, includeSection = true) {
    const token = getUrlMappingToken(tokenName);
    if (!token) return tokenName;
    const { urlSlug, section } = token;
    if (section && includeSection) return `${section}/${urlSlug}`;
    return urlSlug;
}

// ensure token name from url slug or token name. eg - seattle-kraken -> NHLKRAK
export function ensureTokenName(tokenNameOrUrlSlug: string) {
    const urlMapping = getDomainConfigValue('urlMapping');
    if (!urlMapping) return tokenNameOrUrlSlug;
    const { tokens = {} } = urlMapping;
    return Object.keys(tokens).find(tokenName => tokens[tokenName]?.urlSlug === tokenNameOrUrlSlug) || tokenNameOrUrlSlug;
}

export function getActiveAdProvider() {
    return getDomainConfigValue('activeAdProvider');
}

export function shouldDisplayAds(tokenName: string) {
    return tokenName !== 'RTBCORP' && tokenName !== 'FCPSG';
}

export function getPlaywireAd(location: string) {
    const playwireAd = getDomainConfigValue('playWireAd');
    if (!playwireAd) return;

    const ads: PlaywireAd[] = [
        { divId: 'pw-sky', sizes: [300, 600], location: 'top_right', type: 'standard_iab_rght1' },
        { divId: 'pw-sky2', sizes: [300, 600], location: 'mid_right', type: 'standard_iab_rght1' },
        {
            divId: 'pw-leaderboard',
            sizes: [
                [728, 90],
                [320, 50],
            ],
            location: 'top_center',
            type: 'standard_iab_cntr1',
        },
        {
            divId: 'pw-bottom_rail',
            sizes: [
                [320, 50],
                [728, 90],
                [970, 90],
                [320, 100],
            ],
            location: 'btm_sticky',
            type: 'standard_iab_foot1',
        },
        {
            divId: 'pw-med_rect',
            sizes: [
                [320, 50],
                [728, 90],
                [320, 250],
            ],
            location: 'mid_center',
            type: 'standard_iab_cntr1',
        },
        {
            divId: 'pw-med_rect2',
            sizes: [
                [320, 50],
                [728, 90],
                [320, 250],
            ],
            location: 'mid_center_card',
            type: 'standard_iab_cntr2',
        },
    ];

    return ads.find(ad => ad.location === location);
}
